
import { RecurlyPlan } from "@/lib/recurly";
import Checklist from "@/components/Checklist/Checklist";
import Flex from "@/components/Flex/Flex";
import Tabs from "@/components/Tabs/Tabs";
import { useEffect, useState } from "react";
import { LabeledSwitch } from "@/components/forms/LabeledSwitch/LabeledSwitch";
import Divider from "@/components/Divider/Divider";
import Plan from "../Plan";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";

import styles from "./SwitchablePlan.module.css";


const STANDARD_PLAN_FEATURES = [
  { label: "Optimized Shipment Flow", checked: true },
  { label: "Inventory Management", checked: true },
  { label: "Shipments Manager", checked: true },
  { label: "Sales Analytics", checked: true }
];

const PROFESSIONAL_PLAN_FEATURES = [
  { label: "Everything in Standard Plan PLUS", checked: true },
  { label: "LTL Shipments", checked: true },
  { label: "2D Barcodes", checked: true },
  { label: "Early access to Beta features", checked: true }
];

// if you have one of the 'normal' plans selected, we'll
// let you switch between them in the UI with a Professional/Standard,
// and monthly/annual selector
function planIsSwitchable(plan: RecurlyPlan): boolean {
  switch (plan.code) {
    case "standard-monthly-launchspecial":
    case "standard-annual-launchspecial":
    case "professional-monthly-launchspecial":
    case "professional-annual-launchspecial":
      return true;
  }
  return false;
}

export interface SwitchablePlanProps {
  plan: RecurlyPlan;
  setPlanFromCode: (planCode: string) => void;
}

export function SwitchablePlan(props: SwitchablePlanProps) {
  const { plan, setPlanFromCode } = props;

  const [monthlyOrAnnual, setMonthlyOrAnnual] = useState<"Monthly" | "Annual">("Monthly");
  const [activeTab, setActiveTab] = useState<"professional" | "standard">(plan?.code.startsWith("professional") ? "professional" : "standard");

  useEffect(() => {
    if (plan) {
      setActiveTab(plan.code.startsWith("professional") ? "professional" : "standard");
      setMonthlyOrAnnual((plan.interval_unit === "months" && plan.interval_length === 12) ? "Annual" : "Monthly");
    }
  }, [plan]);

  const changePlan = (activeTab: "professional" | "standard", monthlyOrAnnual: "Monthly" | "Annual") => {
    if (activeTab === "professional") {
      if (monthlyOrAnnual === "Annual") {
        setPlanFromCode("professional-annual-launchspecial");
      } else if (monthlyOrAnnual === "Monthly") {
        setPlanFromCode("professional-monthly-launchspecial");
      }
    } else if (activeTab === "standard") {
      if (monthlyOrAnnual === "Annual") {
        setPlanFromCode("standard-annual-launchspecial");
      } else if (monthlyOrAnnual === "Monthly") {
        setPlanFromCode("standard-monthly-launchspecial");
      }
    }
  }

  if (!plan) {
    return <LoadingSpinner/>;    
  }

  const intervalFrequency = plan.interval_length === 1 ? "Monthly" : "Annually";
  const intervalUnits = plan.interval_length === 1 ? "month" : "months";
  let intervalCost = (plan.currencies[0].unit_amount / plan.interval_length).toFixed(2);

  // determine the first billing date after the free trial ends
  const firstBillingDate = new Date();
  
  if (plan.trial_unit === "months") {
    firstBillingDate.setMonth(firstBillingDate.getMonth() + plan.trial_length);
  } else {
    // calculate trial length in days
    firstBillingDate.setDate(firstBillingDate.getDate() + plan.trial_length);
  }

  const isProfessional997WEbinarDeal = plan.code === "professional-997-webinaroffer";
  const totalCost = plan.currencies[0].unit_amount.toFixed(2);
  const totalDueToday = plan.trial_length > 0 ? "0.00" : totalCost;

  if (!planIsSwitchable(plan)) {
    return <Plan plan={plan}/>
  }

  return (
    <Flex direction="column" gap={2} className={styles.container}>
      <Tabs
        tabs={
          [
            {
              key: "professional",
              label: "Professional",
            },
            {
              key: "standard",
              label: "Standard",
            }
          ]
        }
        activeTab={activeTab}
        stretchTabsToCover={true}
        setActiveTab={(tab: "professional" | "standard") => {
          setActiveTab(tab);
          changePlan(tab, monthlyOrAnnual);
        }}
      />

      <LabeledSwitch
        options={["Monthly", "Annual"]}
        selectedOption={monthlyOrAnnual}
        setSelectedOption={(option: "Monthly" | "Annual") => {
          setMonthlyOrAnnual(option);
          changePlan(activeTab, option);
        }}
      />

      <div>
        <Flex justify="space-between">
          <Flex direction="column">
            <span className="heading strong">{plan.name}</span>
            <div className={`weak`}>Billed {intervalFrequency}</div>
          </Flex>
          {!isProfessional997WEbinarDeal && (
            <Flex direction="column" align="end">
              <span className="heading strong">
                ${intervalCost}
              </span>
              <div className={`weak`}>
                x {plan.interval_length} {intervalUnits}
              </div>
            </Flex>
          )}
        </Flex>
      </div>

      <Divider/>

      <div>
        <Flex justify="space-between">
          <Flex direction="column">
            <span className="heading strong">{"Total Due Today"}</span>
            <div className={`weak`}>
              {plan.trial_length}-day free trial, then
              Billed {intervalFrequency} @ ${totalCost}
              <br />
              on {firstBillingDate.toLocaleDateString("en-us", { year: "numeric", month: "long", day: "numeric" })}
            </div>
          </Flex>
          <Flex direction="column" align="end">
            <span className="heading strong">
              ${totalDueToday}
            </span>
          </Flex>
        </Flex>
      </div>

      <Divider/>

      <Checklist
        className={styles.checklist}
        color="primary"
        items={plan.code.startsWith("professional") ? PROFESSIONAL_PLAN_FEATURES : STANDARD_PLAN_FEATURES}
      />
    </Flex>
  );
}
